/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useLocation} from 'react-router'
import {
    Box,
    Heading,
    Text,
    Stack,
    Badge,
    Flex,
    Button,
    Divider,
    Grid,
    Skeleton,
    Image,
    Link as ChakraLink
} from '@salesforce/retail-react-app/app/components/shared/ui'
import DynamicImage from '@app/components/dynamic-image'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCustomerOrders, useProducts} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {usePageUrls, useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import PageActionPlaceHolder from '../../components/page-action-placeholder'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ChevronRightIcon} from '@app/components/icons'
import {OrdersIcon, PlusIcon} from '../../components/custom-icons'
import Pagination from '../../components/pagination'
import PropTypes from 'prop-types'
import {DEFAULT_ORDERS_SEARCH_PARAMS} from '@salesforce/retail-react-app/app/constants'

import {
    getConvertedCurrencyCode,
    getConvertedPrice,
    formatPrice,
    getOrderConversionRate
} from '@app/utils/price-utils'

import {
    ORDER_STATUSES,
    SHIPPING_STATUSES,
    DEFAULT_SHIPPING_STATUS,
    GIFT_CARD_ALT
} from '../../constants'
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

// Utils
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

/**
 * OrderProductImages is a React functional component that displays a grid of product images
 * based on the provided product items. It fetches product data using the `useProducts` hook
 * and displays the images in a responsive grid layout. The component initially shows a limited
 * number of images and provides an option to display more images if available.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.productItems - An array of product items, each containing a `productId`.
 *
 * @returns {JSX.Element} A React component that renders a grid of product images with a "Show more items" button.
 *
 * @example
 * <OrderProductImages productItems={[{productId: '123'}, {productId: '456'}]} />
 *
 * @component
 */
const OrderProductImages = ({productItems = [], isGiftCertificateImage = false}) => {
    const {formatMessage} = useIntl()
    const [numberOfItemsVisible, setNumberOfItemsVisible] = useState(3)

    const ids = productItems?.map((item) => item.productId).join(',') ?? ''
    const {data: {data: products} = {}, isLoading} = useProducts(
        {
            parameters: {
                ids: ids
            }
        },
        {
            enabled: productItems?.length > 0
        }
    )

    const images = products?.map((product) => {
        return product?.imageGroups?.find((group) => group.viewType === 'front')?.images[0]
    })

    return (
        <>
            <Grid templateColumns={{base: 'repeat(auto-fit, 88px)'}} gap={16} pb={4}>
                {!isLoading &&
                    products &&
                    images.map(
                        (image, index) =>
                            image &&
                            index < numberOfItemsVisible && (
                                <DynamicImage
                                    key={index}
                                    src={`${image.disBaseLink || image.link}[?sw={width}&q=60]`}
                                    widths={['384px', '384px', '400px', '360px', '480px', '505px']}
                                    imageProps={{
                                        alt: image.alt,
                                        loading: 'lazy'
                                    }}
                                />
                            )
                    )}

                {/* Gift card image */}
                {isGiftCertificateImage && (
                    <Image
                        src={getAssetUrl('static/img/cart/gift-card.webp')}
                        alt={formatMessage(GIFT_CARD_ALT)}
                        width="85px"
                    />
                )}
            </Grid>
            {!isLoading && products && images.length > numberOfItemsVisible && (
                <HideOnDesktop>
                    <Button
                        variant={'linkSecondary'}
                        rightIcon={<PlusIcon boxSize="12px" />}
                        textTransform={'uppercase'}
                        m={'-8px 0 8px'}
                        onClick={() => {
                            setNumberOfItemsVisible(productItems.length)
                        }}
                    >
                        {formatMessage({
                            defaultMessage: 'Show more items',
                            id: 'account_order_history.show_more_items'
                        })}
                    </Button>
                </HideOnDesktop>
            )}
        </>
    )
}
OrderProductImages.propTypes = {
    productItems: PropTypes.array,
    isGiftCertificateImage: PropTypes.bool
}

const onClient = typeof window !== 'undefined'

/**
 * AccountOrderHistory is a React functional component that renders the order history page for a user's account.
 * It utilizes various hooks and components to fetch and display the user's past orders, including their details,
 * shipment status, and tracking information. The component also handles loading states and provides a placeholder
 * message when no orders have been placed yet.
 *
 * The component makes use of the following hooks:
 * - `useLocation`: To access the current location object, which contains information about the URL.
 * - `useIntl`: To format messages and dates according to the user's locale.
 * - `useNavigation`: To navigate programmatically within the application.
 * - `useCurrentCustomer`: To fetch the current customer's data, including their customer ID.
 * - `useSearchParams`: To manage and retrieve search parameters for pagination.
 * - `useCustomerOrders`: To fetch the customer's orders based on the customer ID and pagination parameters.
 * - `usePageUrls`: To generate URLs for pagination based on the total number of orders and the limit per page.
 *
 * The component displays a loading skeleton while the orders are being fetched. Once the data is available, it
 * renders a list of orders with details such as order date, order number, shipment status, number of items, total
 * amount, and shipping address. Each order can be expanded to view more details, and users can navigate to the
 * order details page.
 *
 * If there are no orders, a placeholder message is shown with a button to continue shopping.
 *
 * The component also handles pagination, allowing users to navigate through multiple pages of orders if applicable.
 *
 * @component
 * @returns {JSX.Element} The rendered order history page component.
 */
const AccountOrderHistory = () => {
    const location = useLocation()
    const {formatMessage, formatDate} = useIntl()
    const navigate = useNavigation()
    const {usePageLoadAndCookies} = useGTM()
    const intl = useIntl()

    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer

    const searchParams = useSearchParams(DEFAULT_ORDERS_SEARCH_PARAMS)
    const {limit, offset} = searchParams[0]

    const {data: {data: orders, ...paging} = {}, isLoading} = useCustomerOrders(
        {
            parameters: {customerId, limit, offset}
        },
        {enabled: onClient && !!customerId}
    )

    const hasOrders = orders?.length > 0

    const pageUrls = usePageUrls({total: paging.total, limit})

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [customer, searchParams.offset])

    usePageLoadAndCookies('account')

    return (
        <Stack spacing={24} data-testid="account-order-history-page">
            <Box>
                <Heading
                    as="h1"
                    display="inline-block"
                    size="3xl"
                    textTransform="uppercase"
                    tabIndex="0"
                >
                    <FormattedMessage
                        defaultMessage="Order History"
                        id="account_order_history.title.order_history"
                    />
                </Heading>
            </Box>

            {isLoading ? (
                [1, 2, 3].map((i) => (
                    <Stack key={i} spacing={4} layerStyle="cardBordered">
                        <Stack spacing={2}>
                            <Skeleton h="20px" w="112px" />
                            <Skeleton h="20px" w="200px" />
                        </Stack>
                        <Grid templateColumns={{base: 'repeat(auto-fit, 88px)'}} gap={4}>
                            {Array.from(Array(4).keys()).map((i) => (
                                <Skeleton key={i} w="88px" h="88px" />
                            ))}
                        </Grid>
                        <Skeleton h="20px" w="200px" />
                    </Stack>
                ))
            ) : (
                <>
                    {hasOrders && (
                        <Stack spacing={16}>
                            {orders?.map((order) => {
                                const isCancelledOrFailedOrder = ['cancelled', 'failed'].includes(
                                    order.status
                                )

                                const orderProductItems = order?.productItems || []
                                const orderGiftCertificateItems = order?.giftCertificateItems || []
                                const productItemsAmount = orderProductItems.length
                                const giftCertificateItemsAmount = orderGiftCertificateItems.length
                                const orderItemsAmount =
                                    productItemsAmount + giftCertificateItemsAmount
                                const isOrderWithGiftCertificate = giftCertificateItemsAmount > 0
                                const isOrderItems = orderItemsAmount > 0

                                const isGiftCertificateShipment =
                                    giftCertificateItemsAmount &&
                                    orderGiftCertificateItems[0]?.shipmentId ===
                                        order?.shipments[0]?.shipmentId
                                const shippedToEmail = isGiftCertificateShipment
                                    ? orderGiftCertificateItems[0]?.recipientEmail
                                    : null
                                const shippedTo = isGiftCertificateShipment
                                    ? shippedToEmail
                                    : `${order?.shipments[0].shippingAddress.firstName} ${order?.shipments[0].shippingAddress.lastName}`

                                const conversionsRate = getOrderConversionRate(order)
                                const shipToCountryCode = order?.c_internationalShippingCountryCode

                                return (
                                    <Stack
                                        key={order.orderNo}
                                        spacing={16}
                                        layerStyle="accountCard"
                                    >
                                        <Box>
                                            <Flex
                                                justifyContent={'space-between'}
                                                alignItems={'center'}
                                                gap={4}
                                                mb={16}
                                            >
                                                <Text
                                                    variant="eyeBrowLarge"
                                                    textTransform={'uppercase'}
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Ordered: {date}"
                                                        id="account_order_history.label.ordered_date"
                                                        values={{
                                                            date: formatDate(
                                                                new Date(order.creationDate),
                                                                {
                                                                    year: 'numeric',
                                                                    day: 'numeric',
                                                                    month: 'long'
                                                                }
                                                            )
                                                        }}
                                                    />
                                                </Text>
                                                <Button
                                                    as={Link}
                                                    to={`/account/orders/${order.orderNo}`}
                                                    variant="linkSecondary"
                                                    rightIcon={
                                                        <ChevronRightIcon boxSize={5} mx={-1.5} />
                                                    }
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="View details"
                                                        id="account_order_history.link.view_details"
                                                    />
                                                </Button>
                                            </Flex>
                                            <Text variant={'bodyBase2'} mb={8}>
                                                <FormattedMessage
                                                    defaultMessage="Order Number: {orderNumber}"
                                                    id="account_order_history.label.order_number"
                                                    values={{orderNumber: order.orderNo}}
                                                />
                                            </Text>
                                            {['completed', 'cancelled', 'failed'].includes(
                                                order.status
                                            ) && (
                                                <Text>
                                                    <Badge
                                                        variant={
                                                            isCancelledOrFailedOrder
                                                                ? 'fail'
                                                                : 'fresh'
                                                        }
                                                        mb={isCancelledOrFailedOrder ? 0 : 10}
                                                    >
                                                        {formatMessage(
                                                            ORDER_STATUSES[order.status]
                                                        )}
                                                    </Badge>
                                                </Text>
                                            )}
                                            {!isCancelledOrFailedOrder && (
                                                <Stack
                                                    direction={['column', 'column', 'row']}
                                                    alignItems={[
                                                        'flex-start',
                                                        'flex-start',
                                                        'center'
                                                    ]}
                                                    justifyContent={'flex-start'}
                                                    spacing={[8, 8, 24]}
                                                >
                                                    {order.shipments.length > 1 &&
                                                        order.shipments.map((shipment, index) => {
                                                            const shippingStatusKey = (
                                                                shipment.c_shipmentShippingStatus ||
                                                                shipment.shippingStatus
                                                            ).toUpperCase()
                                                            const shippingStatus =
                                                                SHIPPING_STATUSES[shippingStatusKey]

                                                            return (
                                                                <Flex
                                                                    alignItems={'center'}
                                                                    key={shipment.shipmentNo}
                                                                >
                                                                    <Text variant={'bodyBase3'}>
                                                                        <FormattedMessage
                                                                            defaultMessage="Shipment: {shipment}"
                                                                            id="account_order_history.label.shipment_number"
                                                                            values={{
                                                                                shipment: index + 1
                                                                            }}
                                                                        />
                                                                    </Text>
                                                                    <Badge variant="fresh" ml={8}>
                                                                        {formatMessage(
                                                                            shippingStatus ||
                                                                                SHIPPING_STATUSES[
                                                                                    DEFAULT_SHIPPING_STATUS
                                                                                ]
                                                                        )}
                                                                    </Badge>
                                                                </Flex>
                                                            )
                                                        })}
                                                    {order.shipments.length === 1 && (
                                                        <Flex alignItems={'center'}>
                                                            <Text variant={'bodyBase3'}>
                                                                <FormattedMessage
                                                                    defaultMessage="Shipment: {shipment}"
                                                                    id="account_order_history.label.shipment_number"
                                                                    values={{shipment: 1}}
                                                                />
                                                            </Text>
                                                            <Badge variant="fresh" ml={8}>
                                                                {formatMessage(
                                                                    SHIPPING_STATUSES[
                                                                        (
                                                                            order.shipments[0]
                                                                                .c_shipmentShippingStatus ||
                                                                            order.shipments[0]
                                                                                .shippingStatus
                                                                        ).toUpperCase()
                                                                    ] ||
                                                                        SHIPPING_STATUSES[
                                                                            DEFAULT_SHIPPING_STATUS
                                                                        ]
                                                                )}
                                                            </Badge>
                                                        </Flex>
                                                    )}
                                                </Stack>
                                            )}
                                        </Box>
                                        {isOrderItems && (
                                            <>
                                                <OrderProductImages
                                                    productItems={order?.productItems}
                                                    isGiftCertificateImage={
                                                        isOrderWithGiftCertificate
                                                    }
                                                />

                                                <Stack
                                                    direction={['column', 'column', 'row']}
                                                    alignItems={[
                                                        'flex-start',
                                                        'flex-start',
                                                        'center'
                                                    ]}
                                                    wrap={'wrap'}
                                                    spacing={[4, 4, 8]}
                                                    gap={[0, 0, 8]}
                                                    divider={
                                                        <Divider
                                                            visibility={[
                                                                'hidden',
                                                                'hidden',
                                                                'visible'
                                                            ]}
                                                            orientation={'vertical'}
                                                            h={{base: 0, lg: 4}}
                                                        />
                                                    }
                                                >
                                                    <Text variant={'bodyBase3'}>
                                                        <FormattedMessage
                                                            description="# item(s) in order"
                                                            defaultMessage="{itemCount, plural, =0 {0 items} one {# item} other {# items}}"
                                                            values={{itemCount: orderItemsAmount}}
                                                            id="account_order_history.label.num_of_items"
                                                        />
                                                    </Text>
                                                    <Text variant={'bodyBase3'}>
                                                        {formatPrice(
                                                            getConvertedPrice(
                                                                order.orderTotal,
                                                                conversionsRate
                                                            ),
                                                            getConvertedCurrencyCode(
                                                                conversionsRate,
                                                                order?.currency
                                                            ),
                                                            conversionsRate,
                                                            shipToCountryCode,
                                                            intl
                                                        )}
                                                    </Text>
                                                    <Text variant={'bodyBase3'}>
                                                        <FormattedMessage
                                                            defaultMessage="Shipped to: {name}"
                                                            id="account_order_history.label.shipped_to"
                                                            values={{
                                                                name: shippedTo
                                                            }}
                                                        />
                                                    </Text>
                                                    {order.shipments.map((shipment, index) => {
                                                        /* TODO get tracking link */
                                                        const trackingLink = `/account/orders#tracking_${shipment.trackingNumber}`

                                                        return shipment.trackingNumber ? (
                                                            <Text
                                                                variant={'bodyBase3'}
                                                                key={shipment.shipmentNo}
                                                            >
                                                                <FormattedMessage
                                                                    defaultMessage="Shipment {shipment} Tracking Number:"
                                                                    id="account_order_history.label.shipment_tracking_number"
                                                                    values={{
                                                                        shipment: index + 1
                                                                    }}
                                                                />{' '}
                                                                <ChakraLink
                                                                    variant="link"
                                                                    href={trackingLink}
                                                                    ml={4}
                                                                    color={'burgundy'}
                                                                    fontWeight={400}
                                                                >
                                                                    {shipment.trackingNumber}
                                                                </ChakraLink>
                                                            </Text>
                                                        ) : null
                                                    })}
                                                </Stack>
                                            </>
                                        )}
                                    </Stack>
                                )
                            })}

                            {hasOrders && orders?.length < paging.total && (
                                <Box pt={4}>
                                    <Pagination
                                        currentURL={`${location.pathname}${location.search}`}
                                        urls={pageUrls}
                                    />
                                </Box>
                            )}
                        </Stack>
                    )}
                </>
            )}

            {!hasOrders && !isLoading && (
                <Box layerStyle="accountCard" data-testid="account-order-history-place-holder">
                    <PageActionPlaceHolder
                        icon={<OrdersIcon boxSize={8} />}
                        heading={formatMessage({
                            defaultMessage: "You haven't placed an order yet.",
                            id: 'account_order_history.heading.no_order_yet'
                        })}
                        text={formatMessage({
                            defaultMessage:
                                'Once you place an order the details will show up here.',
                            id: 'account_order_history.description.once_you_place_order'
                        })}
                        buttonText={formatMessage({
                            defaultMessage: 'Continue Shopping',
                            id: 'account_order_history.button.continue_shopping'
                        })}
                        buttonProps={{
                            variant: 'primary'
                        }}
                        onButtonClick={() => navigate('/')}
                    />
                </Box>
            )}
        </Stack>
    )
}

AccountOrderHistory.getTemplateName = () => 'account-order-history'

export default AccountOrderHistory
